import { splitMathSegments } from './mathTextSegments'

// A four-sentence tutor reply reads as a wall of text in one chat bubble, so the
// Explain chat shows at most two sentences per bubble. This is presentation
// only: the stored message, the message count and the chat limit are all
// untouched, and the model's wording is never edited.

export const MAX_SENTENCES_PER_BUBBLE = 2

const TERMINATORS = '.!?'

// A closing mark that belongs to the sentence it follows, so `(see above.)`
// does not start the next bubble with a stray bracket.
const CLOSERS = '"\')]\u2019\u201d'

// Words that end in a period without ending a sentence. Compared with the
// periods removed, so `e.g.` is matched by its `eg` form.
const ABBREVIATIONS = new Set([
  'dr', 'mr', 'mrs', 'ms', 'prof', 'sr', 'jr', 'st',
  'eg', 'ie', 'etc', 'vs', 'cf', 'al', 'approx', 'fig', 'eq', 'inc', 'ltd'
])

const isTerminator = (char) => TERMINATORS.includes(char)

const endsAbbreviation = (before) => {
  const word = before.match(/[A-Za-z.]+$/)
  if (!word) return false
  return ABBREVIATIONS.has(word[0].replace(/\./g, '').toLowerCase())
}

const terminatesSentence = (mark, before, after) => {
  // The mark has to be the end of a line or be followed by a space, which is
  // what separates `3.5` and `x.y` from the end of a thought.
  if (after && !/^\s/.test(after)) return false

  // `...` is a pause inside a sentence; `?!` and `!!` are emphatic endings.
  if (/^\.\.+$/.test(mark)) return false

  if (mark === '.') {
    // A decimal point, for the case where the notation was never delimited.
    if (/\d$/.test(before) && /^\d/.test(after)) return false
    // A list marker (`1.`, `2.`) rather than a sentence.
    if (/^\d+$/.test(before.trimStart())) return false
    if (endsAbbreviation(before)) return false
  }

  return true
}

// Sentences plus the whitespace that preceded each of them, so grouping can
// rejoin two sentences the way the model wrote them — a line break stays a line
// break instead of collapsing a list into one run-on line.
const splitSentenceParts = (input) => {
  const parts = []
  let buffer = ''

  const flush = () => {
    const text = buffer.trim()
    if (text) {
      parts.push({ text, gap: buffer.slice(0, buffer.length - buffer.trimStart().length) })
    }
    buffer = ''
  }

  splitMathSegments(input).forEach((segment) => {
    if (segment.type === 'math') {
      buffer += segment.value
      return
    }

    const value = segment.value
    let index = 0
    while (index < value.length) {
      const char = value[index]
      if (!isTerminator(char)) {
        buffer += char
        index += 1
        continue
      }

      const before = buffer
      let markEnd = index
      while (markEnd < value.length && isTerminator(value[markEnd])) markEnd += 1
      const mark = value.slice(index, markEnd)
      buffer += mark
      index = markEnd

      while (index < value.length && CLOSERS.includes(value[index])) {
        buffer += value[index]
        index += 1
      }

      if (terminatesSentence(mark, before, value.slice(index))) flush()
    }
  })

  flush()
  return parts
}

/**
 * Splits text into display sentences. Math spans are opaque, so a period inside
 * `$…$`, `\(…\)` or `\[…\]` never ends a sentence.
 */
export const splitSentences = (input) => splitSentenceParts(input).map((part) => part.text)

/**
 * Groups text into bubbles of at most `maxSentences` sentences. Text with no
 * sentence ending comes back as a single bubble; blank text comes back empty.
 */
export const groupSentencesIntoBubbles = (input, maxSentences = MAX_SENTENCES_PER_BUBBLE) => {
  const limit = Number.isInteger(maxSentences) && maxSentences > 0
    ? maxSentences
    : MAX_SENTENCES_PER_BUBBLE
  const parts = splitSentenceParts(input)
  const bubbles = []

  for (let start = 0; start < parts.length; start += limit) {
    bubbles.push(parts.slice(start, start + limit).reduce((bubble, part, offset) => (
      offset === 0
        ? part.text
        : `${bubble}${part.gap.includes('\n') ? '\n' : ' '}${part.text}`
    ), ''))
  }

  return bubbles
}
